import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import ahaMain from '../Images/AHA.jpeg';
import aha1 from '../Images/AHA1.jpeg';
import heroImage from '../Images/hampage.jpg';
import Footer from '../Pages/Footer';

const imageDetails = {
  [ahaMain]: {
    category: 'Hematology Analyzers',
    heading: 'Auto Hematology Analyzer Part 5',
    title: 'Model: MMT HAM 500',
    description:
      'The MMT HAM 500 is a reliable and high-performance automatic hematology analyzer designed for precise quantitative analysis of blood cells in clinical laboratories. It provides accurate measurement of hematology parameters including WBC, RBC, PLT, HGB, and supports 3-part white blood cell differentiation for efficient routine diagnostics.',
    sections: {
      "Smart Display & Operation": {
        icon: "🖥️",
        items: [
          "Color touch screen interface",
          "Parameters and histograms displayed on the same screen",
          "User-friendly operation and quick navigation",
          "Chinese and English language support",
          "Easy review and management of test results",
        ],
      },

      "Intelligent Printing & Reporting": {
        icon: "🖨️",
        items: [
          "Built-in thermal printer",
          "Automatic storage and review of test results",
          "Supports multiple printing methods",
          "Part report generation for laboratory workflow",
          "Convenient result management and printing functionality",
        ],
      },

      "Accurate Measurement & Analysis": {
        icon: "🧪",
        items: [
          "Cyanide-free SFT hemoglobin measurement method",
          "Environment-friendly hemoglobin testing",
          "High-precision blood parameter analysis",
          "Reliable screening support for clinical laboratories",
          "Consistent and stable testing performance",
        ],
      },

      "Advanced Cleaning & Anti-Clogging System": {
        icon: "💧",
        items: [
          "High-pressure burning jewel hole technology",
          "High-pressure reverse rinsing mechanism",
          "Soaking-based clog removal functionality",
          "Automatic sample probe cleaning",
          "Improves analyzer stability and maintenance efficiency",
        ],
      },

      "Connectivity & External Support": {
        icon: "🔌",
        items: [
          "Supports mouse, keyboard, and external printer",
          "VGA interface for external monitor or projector connection",
          "Flexible laboratory integration capabilities",
          "Easy connection with external devices",
          "Enhanced operational convenience",
        ],
      },

      "Reference Range Management": {
        icon: "📋",
        items: [
          "Newborn",
          "Children",
          "Adult Women",
          "Adult Men",
          "General Use",
        ],
      },
    },
  },

  [aha1]: {
    category: 'Hematology Analyzers',
    heading: 'Auto Hematology Analyzer Part 3',
    title: 'Model: MMT HAM 300',
    description:
      'The MMT HAM 300 is a compact and efficient automatic hematology analyzer designed for reliable blood cell analysis in clinical laboratories and diagnostic centers. Built with intelligent operation, fast processing, and accurate measurement technology, the analyzer supports routine hematology testing with high stability and user-friendly performance.',
    sections: {
      "Display & Operation": {
        icon: "🖥️",
        items: [
          "User-friendly operation interface",
          "Fast and simple sample processing",
          "Built-in thermal printer support",
          "External device connectivity support",
          "Easy data viewing and management",
        ],
      },

      "Performance": {
        icon: "📈",
        items: [
          "Throughput up to 60 samples per hour",
          "Sample consumption: 20uL",
          "Supports multiple printing methods",
          "Supports both whole blood mode and pre-diluted sampling mode",
          "Accurate and stable hematology testing",
          "Efficient routine laboratory performance",
        ],
      },

      "Environment & Calibration": {
        icon: "🌎",
        items: [
          "Requires only 2 reagents for operation",
          "Integrated calibration functionality",
          "Stable system performance with reduced maintenance",
          "Designed for long-term laboratory use",
          "Reliable operation for clinical environments",
        ],
      },

      "Intelligent Printing & Reporting": {
        icon: "🖨️",
        items: [
          "Built-in printer for instant report printing",
          "Supports external printing functionality",
          "Convenient result storage and management",
          "Fast report generation for laboratory workflow",
          "Simplifies daily testing operations",
        ],
      },

      "Connectivity & Data Management": {
        icon: "📡",
        items: [
          "USB and LAN port support",
          "Compatible with HL7 protocol",
          "Easy integration with hospital information systems",
          "Smooth data communication and transfer",
          "Supports modern laboratory connectivity requirements",
        ],
      },

      "Quality Control & Reliability": {
        icon: "✅",
        items: [
          "Integrated quality control management",
          "Calibration management functionality",
          "Reliable daily laboratory operation",
          "Designed for accurate and continuous performance",
          "Suitable for hospitals, clinics, and diagnostic laboratories",
        ],
      },
    },
  },
};

const AhaDetail = () => {
  const allImages = [ahaMain, aha1];
  const [mainImage, setMainImage] = useState(ahaMain);
  const [relatedImages, setRelatedImages] = useState(allImages.filter((img) => img !== ahaMain));
  const [isMobile, setIsMobile] = useState(false);
  const introRef = useRef(null);

  useLayoutEffect(() => {
    setIsMobile(window.innerWidth <= 768);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleImageClick = (clickedImage) => {
    setMainImage(clickedImage);
    setRelatedImages(allImages.filter((img) => img !== clickedImage));
    setTimeout(() => {
      introRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const detail = imageDetails[mainImage];
  const sections = detail?.sections || {};

  return (
    <>
      <Helmet>
        <title>Auto Hematology Analyzer Machine | Mindron Meditech</title>
        <meta
          name="description"
          content="Explore Mindron Meditech auto hematology analyzer machines for accurate blood cell analysis, WBC, RBC, PLT, HGB testing, 3-part differentiation, printing, connectivity, and laboratory diagnostics."
        />
        <link rel="canonical" href="https://www.mindronmeditech.com/products/autohematologyanalyzer" />
      </Helmet>

      <div style={{ fontFamily: 'sans-serif', margin: 0, padding: 0, overflowX: 'hidden' }}>

        {/* ── HERO ── */}
        <div style={{
          height: isMobile ? '30vh' : '80vh',
          width: '100%',
          overflow: 'hidden',
          position: 'relative',
        }}>
          <img
            src={heroImage}
            alt="Hero"
            style={{ width: '100%', height: '100%', objectFit: 'fill', display: 'block' }}
          />
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(to top, rgba(0,20,60,0.5) 0%, transparent 60%)',
          }} />
        </div>

        {/* ── PRODUCT INTRO ── */}
        <div ref={introRef} style={{
          display: 'flex',
          flexWrap: isMobile ? 'wrap' : 'nowrap',
          gap: '40px',
          padding: isMobile ? '32px 6%' : '48px 6%',
          alignItems: 'flex-start',
        }}>
          {/* Left: text */}
          <div style={{ flex: 1, minWidth: '260px' }}>
            <div style={{
              display: 'inline-block',
              backgroundColor: '#e8f0fe',
              color: '#1a56db',
              fontSize: '13px',
              fontWeight: '600',
              padding: '4px 14px',
              borderRadius: '20px',
              marginBottom: '14px',
            }}>
              {detail?.category}
            </div>
            <h1 style={{
              fontSize: isMobile ? '26px' : '38px',
              fontWeight: '800',
              color: '#0a1628',
              margin: '0 0 8px',
              lineHeight: 1.2,
            }}>
              {detail?.title}
            </h1>
            <h2 style={{
              fontSize: isMobile ? '16px' : '20px',
              fontWeight: '500',
              color: '#444',
              margin: '0 0 20px',
            }}>
              {detail?.heading}
            </h2>
            <p style={{ fontSize: '15px', color: '#555', lineHeight: '1.8', maxWidth: '520px' }}>
              {detail?.description}
            </p>
          </div>

          {/* Right: product image */}
          <div style={{
            flex: '0 0 auto',
            width: isMobile ? '100%' : '340px',
            background: '#f4f6fb',
            borderRadius: '14px',
            padding: '24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img
              src={mainImage}
              alt="AHA Device"
              style={{ width: '100%', maxHeight: '260px', objectFit: 'contain', borderRadius: '8px' }}
            />
          </div>
        </div>

        {/* ── KEY FEATURES GRID ── */}
        <div style={{ padding: isMobile ? '16px 6% 40px' : '16px 6% 56px' }}>
          <div style={{ width: '50px', height: '5px', backgroundColor: '#cc0000', borderRadius: '2px', marginBottom: '12px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#0a1628', marginBottom: '28px' }}>
            Key Features
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '20px',
          }}>
            {Object.entries(sections).map(([sectionName, sectionData], idx) => (
              <div key={idx} style={{ background: '#f4f6fb', borderRadius: '12px', padding: '22px 20px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <span style={{ fontSize: '20px' }}>{sectionData.icon}</span>
                  <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#0a1628', margin: 0 }}>
                    {sectionName}
                  </h3>
                </div>
                <ul style={{ margin: 0, paddingLeft: '18px', listStyle: 'disc' }}>
                  {sectionData.items.map((item, i) => (
                    <li key={i} style={{ fontSize: '13px', color: '#555', lineHeight: '1.8' }}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* ── RELATED PRODUCTS ── */}
        <div style={{ padding: isMobile ? '0 6% 48px' : '0 6% 60px' }}>
          <div style={{ width: '50px', height: '5px', backgroundColor: '#cc0000', borderRadius: '2px', marginBottom: '12px' }} />
          <h2 style={{ fontSize: '22px', fontWeight: '800', color: '#0a1628', marginBottom: '24px' }}>
            Related Products
          </h2>
          <div style={{
            display: 'flex',
            gap: '20px',
            overflowX: 'auto',
            overflowY: 'hidden',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            paddingBottom: '4px',
          }}>
            {relatedImages.map((img, index) => (
              <div
                key={index}
                onClick={() => handleImageClick(img)}
                style={{
                  flex: '0 0 auto',
                  width: '220px',
                  background: '#f4f6fb',
                  borderRadius: '12px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.13)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.07)';
                }}
              >
                <div style={{
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '150px',
                }}>
                  <img
                    src={img}
                    alt={`Related ${index}`}
                    style={{ maxWidth: '100%', maxHeight: '120px', objectFit: 'contain' }}
                  />
                </div>
                <div style={{ background: '#001f4d', padding: '10px 14px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#fff', fontWeight: '600' }}>
                    {imageDetails[img]?.title || `Product ${index + 1}`}
                  </p>
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#aac4ff' }}>
                    {imageDetails[img]?.category}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Footer />

        <style>{`
          *::-webkit-scrollbar { display: none; }
          * { scrollbar-width: none; }
          body { overflow-x: hidden; }
        `}</style>
      </div>
    </>
  );
};

export default AhaDetail;